import { forwardRef, useImperativeHandle, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useRect } from 'react-use-rect';
import type { RewardType } from '../../types';
import { Cap } from './cap';
import { Disk } from './disk';
import { Pointer } from './pointer';
import { ColorSchemeCSSVariable, useColorScheme } from './use-color-scheme';
import { useWheelBehaviour } from './use-wheel-behaviour';
import { useWheelRewards } from './use-wheel-rewards';

const INTRINSIC_SIZE = 612;

const DISK_INTRINSIC_RADIUS = 300;

export interface WheelProps {
  selectedWinningRewardId?: string;
  orientation?: WheelOrientation;
  className?: string;
  onSpinStart?: () => void;
  onSpinEnd?: (winningReward: RewardType) => void;
  disabled?: boolean;
  onInteractWhenDisabled?: () => void;
}

export type WheelOrientation = 'horizontal' | 'vertical';

export interface WheelHandle {
  spin: () => void;
  reset: () => void;
}

export const Wheel = forwardRef<WheelHandle, WheelProps>(function Wheel(
  {
    selectedWinningRewardId,
    orientation = 'horizontal',
    className,
    onSpinStart,
    onSpinEnd,
    disabled,
    onInteractWhenDisabled
  },
  ref
) {
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  );
  const [containerRef] = useRect(
    ({ width }) => {
      setContainerWidth(width);
    },
    { resize: true }
  );
  // The wheel scales proportionally to the container width
  // based on it's intrinsic size like it's a raster image.
  const scale = (containerWidth ?? INTRINSIC_SIZE) / INTRINSIC_SIZE;

  const colorScheme = useColorScheme();

  const rewards = useWheelRewards();

  const {
    spindleRef,
    buildDragRootAttributes,
    spin,
    reset,
    handleTransitionEnd
  } = useWheelBehaviour({
    rewards,
    selectedWinningRewardId,
    onSpinStart,
    onSpinEnd,
    disabled,
    onInteractWhenDisabled
  });

  useImperativeHandle(ref, () => ({ spin, reset }), [spin, reset]);

  return (
    <Container
      ref={containerRef}
      style={{
        height: containerWidth,
        ...colorScheme.cssVariables
      }}
      className={className}
    >
      {
        <Stand
          {...buildDragRootAttributes()}
          style={{ transform: `scale(${scale})` }}
          _disablePointerCursor={!!selectedWinningRewardId}
        >
          <Base $orientation={orientation}>
            <Spindle ref={spindleRef} onTransitionEnd={handleTransitionEnd}>
              <Disk radius={DISK_INTRINSIC_RADIUS} rewards={rewards} />
            </Spindle>
            <Pointer />
          </Base>

          <Cap />
        </Stand>
      }
    </Container>
  );
});

const Container = styled.div``;

const Stand = styled.div<{ _disablePointerCursor?: boolean }>`
  display: flex;
  width: ${INTRINSIC_SIZE}px;
  height: ${INTRINSIC_SIZE}px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  position: absolute;
  touch-action: none;
  cursor: ${({ _disablePointerCursor }) =>
    _disablePointerCursor ? 'default' : 'pointer'};
  background:
    linear-gradient(
      0deg,
      var(${ColorSchemeCSSVariable.StandBackground}) 0%,
      var(${ColorSchemeCSSVariable.StandBackground}) 100%
    ),
    linear-gradient(0deg, #f2f2f2, #f2f2f2);
  transform-origin: left top;
`;

const Base = styled.div<{
  $orientation: WheelOrientation;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  position: relative;

  ${({ $orientation }) =>
    $orientation === 'vertical' &&
    css`
      transform: rotate(90deg);
    `}

  &::after {
    content: '';
    pointer-events: none;
    border-radius: 50%;
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    box-shadow: inset 0px 2px 8px 0px rgba(0, 0, 0, 0.25);
  }
`;

const rollingKeyframes = keyframes`
  from {
    transform: rotate(0turn);
  }
  to {
    transform: rotate(1turn);
  }
`;

const Spindle = styled.div`
  overflow: hidden;
  border-radius: 50%;
  transition: transform 6s cubic-bezier(0.15, 0.01, 0, 1);
  animation: ${rollingKeyframes} 180s linear infinite;
  animation-fill-mode: none;
`;
